import { memo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import parse from 'html-react-parser';
import PageNav from '../../../components/common/PageNav';
import LoadingSpinner from '../../../components/loadingSpinner/LoadingSpinner';
import { truncateHtml } from './GlofasNewsList.utils';
import './GlofasNewsList.css';

interface NewsItem {
    id: number;
    title: string;
    body: string;
    image: string | null;
    created: string;
    slug: string;
    category_name: string;
    category_image: string | null;
    sender_name: string;
}

interface NewsApiResponse {
    count: number;
    next: string | null;
    previous: string | null;
    results: NewsItem[];
}

interface GlofasNewsSearchProps {
    query: string;
    startDate: string;
    endDate: string;
    category: string;
    page: number;
    onPageChange: (page: number) => void;
}

export default memo(function GlofasNewsSearch({ query, startDate, endDate, category, page, onPageChange }: GlofasNewsSearchProps) {
    const [results, setResults] = useState<NewsApiResponse | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (query) params.set('query', query);
        if (startDate) params.set('start_date', startDate);
        if (endDate) params.set('end_date', endDate);
        if (category) params.set('category', category);
        params.set('page', String(page));

        fetch(`/api/news/?${params}`)
            .then((res) => res.json())
            .then((data: NewsApiResponse) => {
                setResults(data);
                setLoading(false);
            });
    }, [query, startDate, endDate, category, page]);

    const maxPage = results ? Math.ceil(results.count / 11) : 1;

    return (
        <div className="mt-4 glofas-news-list">
            {loading ? (
                <LoadingSpinner />
            ) : results && results.results.length === 0 ? (
                <p className="text-muted">No results found.</p>
            ) : (
                results?.results.map((news) => (
                    <div key={news.id} className="row mb-4 pb-4 border-bottom">
                        <div className="col-md-9">
                            <h3 style={{ color: 'orange' }}>
                                <Link to={`${news.id}-${news.slug}`} className="ccl-use-case-title text-decoration-none">
                                    {news.title}
                                </Link>
                            </h3>
                            <div>{parse(truncateHtml(news.body))}</div>
                            <small className="text-muted">
                                <i className="fa fa-user me-1"></i>
                                {news.sender_name}
                                &nbsp;&nbsp;
                                <i className="fa fa-calendar me-1"></i>
                                {new Date(news.created).toLocaleDateString()}
                            </small>
                        </div>
                        <div className="col-md-3">
                            {(news.image || news.category_image) && (
                                <img
                                    src={news.image || news.category_image!}
                                    alt={news.title}
                                    className="img-fluid rounded search_img"
                                />
                            )}
                        </div>
                    </div>
                ))
            )}
            {results && results.count > 11 && (
                <PageNav page={page} maxPage={maxPage} onChange={onPageChange} />
            )}
        </div>
    );
});
